import React, { useEffect, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism';

const BLOCKQUOTE_STYLE = {
  background: '#e9ecef',
  padding: '1px 15px',
  borderRadius: '10px',
};

const IMG_STYLE = { maxWidth: '40vw' };
const EM_STYLE = { fontStyle: 'italic' };

const components = {
  a({ href, children, ...props }) {
    if (href && !href.startsWith('http') && href.endsWith('.md')) {
      const view = href.replace(/\.md$/, '');
      return (
        <a
          href={`#${view}`}
          onClick={(e) => { e.preventDefault(); window.location.hash = view; }}
          {...props}
        >
          {children}
        </a>
      );
    }
    return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
  },
  code({ className, children }) {
    const match = /language-(\w+)/.exec(className || '');
    const content = String(children)
      .replace(/\n$/, '')
      .replace(/\n&nbsp;\n/g, '')
      .replace(/\n&nbsp\n/g, '');

    return match ? (
      <SyntaxHighlighter style={nord} language={match[1]} PreTag="div">
        {content}
      </SyntaxHighlighter>
    ) : (
      <SyntaxHighlighter style={nord} language="textile" PreTag="div">
        {String(children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    );
  },
  blockquote({ children, ...props }) {
    return (
      <blockquote style={BLOCKQUOTE_STYLE} {...props}>
        {children}
      </blockquote>
    );
  },
  img({ src, ...props }) {
    return (
      <img
        style={IMG_STYLE}
        src={src?.replace('../../../../public/', '/')}
        alt=""
        {...props}
      />
    );
  },
  em({ children, ...props }) {
    return (
      <span style={EM_STYLE} {...props}>
        {children}
      </span>
    );
  },
};

function MarkdownViewer({ filename }) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMarkdown = useCallback(() => {
    setLoading(true);
    setError(null);

    fetch(`markdown/${filename}.md`)
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load ${filename}.md (${response.status})`);
        return response.text();
      })
      .then((text) => {
        setContent(text);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [filename]);

  useEffect(() => {
    fetchMarkdown();
  }, [fetchMarkdown]);

  if (loading) {
    return (
      <div className="md-status" role="status" aria-live="polite">
        <div className="md-spinner" aria-hidden="true" />
        <span className="md-status-text">Loading…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="md-status md-error" role="alert">
        <p>콘텐츠를 불러오지 못했습니다.</p>
        <p className="md-error-detail">{error}</p>
        <button onClick={fetchMarkdown}>다시 시도</button>
      </div>
    );
  }

  return (
    <div key={filename} className="md-fade-in">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}

export default MarkdownViewer;
